"use client";

import React, { useState } from "react";
import Splash from "../Splash/Splash";
import { useGetPatientById, useUpdatePatientToS } from "@/lib/hooks/usePatient";
import { useAuthStore } from "@/lib/stores/useAuthStore";
import styled from "styled-components";
import { useUserProfile } from "@/lib/hooks/useUser";
import { Patient } from "@clients/velys-infrastructure/api";
import DePuySynthesLogo from "@/assets/logos/DePuySynthes.svg";
import Image from "next/image";
import { useTranslations } from "next-intl";
import Checkbox from "../Checkbox/Checkbox";
import { useMedia } from "@/lib/hooks/useMedia";
import Button from "../Button/Button";
import toast from "react-hot-toast";
import { CollectionStatement } from "@/lib/components/Legal/documents/CollectionStatement";
import { useBoundStore } from "@/lib/stores/useBoundStore";
import { Modal } from "../Modal/Modal";
import { USPatientPrivacyPolicyContent } from "../Modal/USPatientPrivacyPolicyContent";
import { USTermsOfUseContent } from "../Modal/USTermsOfUse";
import { ANZPrivacyPolicyContent } from "../Modal/ANZPrivacyPolicyContent";
import { ANZTermsOfUseContent } from "../Modal/ANZTermsOfUse";
import { EMEAPatientPrivacyContent } from "@/lib/components/Legal/documents/EMEAPatientPrivacy";
import { EMEAPatientTermsOfUseContent } from "@/lib/components/Legal/documents/EMEAPatientTermsOfUse";
import { countryToRegion } from "@/lib/components/Legal/helpers";

interface AcceptTermsProps {
  children: React.ReactNode;
}

const AcceptTermsContainer = styled.div`
  height: 100dvh;
  width: 100vw;

  background-color: ${(props) => props.theme.white};

  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 30px;
  gap: 30px;

  .terms-container {
    height: 85%;
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 0 20px;
    max-width: 100vw;
    margin-bottom: 50px;

    .final-container {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      flex: none;
    }

    .on-click-link {
      text-decoration: underline;
      color: #00b4e1;
      cursor: pointer;
    }

    .collection-ul {
      margin: 0 50px;
    }

    h2 {
      font-size: 1.25rem;
      text-align: center;
    }

    p {
      font-size: 1rem;
    }

    label {
      margin-top: 10px;
    }

    .privacy,
    .collection {
      display: flex;
      flex-direction: column;
      gap: 15px;
      overflow: auto;
      flex: 1;
      min-height: 0;

      > div {
        margin-bottom: 0;
        height: unset;
        overflow: unset;
        max-height: unset;
        padding: 0;

        a {
          word-break: break-word;
        }
      }
    }

    ol {
      padding: 0 20px;
      font-size: 1rem;
      li {
      }
    }

    .next {
      align-self: flex-end;
      flex: none;
      margin: 10px 10px 0 0;
    }

    @media (max-width: 425px) {
      margin-bottom: 100px;

      h2 {
        font-size: 1rem;
      }

      p,
      span,
      ol,
      ul {
        font-size: 0.75rem;
      }
    }
  }
`;

// The EMEA docs render via LegalDocumentBody (no built-in height cap). In a
// modal, cap them to match the US/ANZ content (LegalContainer's 75vh / 92vh).
const ModalScroll = styled.div`
  max-height: 75vh;
  overflow-y: auto;

  @media (max-width: 425px) {
    max-height: 92vh;
  }
`;

export default function AcceptTerms({ children }: AcceptTermsProps) {
  const tTerms = useTranslations("termsOfAcceptance");
  const tComponentText = useTranslations("componentText");
  const tToast = useTranslations("toast");

  const { userProfileQuery } = useUserProfile();
  const { mutate: updateToS, isPending: isSubmittingTerms } =
    useUpdatePatientToS();
  const user_profile = useAuthStore((state) => state.user_profile);
  const patientId =
    userProfileQuery.data?.role !== "patient" ? "" : user_profile?.sub;
  const patientQuery = useGetPatientById(patientId);
  const isModalOpen = useBoundStore((state) => state.isModalOpen);
  const setModalType = useBoundStore((state) => state.setModalType);
  const modalType = useBoundStore((state) => state.modalType);
  const setModalOpen = useBoundStore((state) => state.setModalOpen);
  const userCountry = useBoundStore((state) => state.userCountry);
  const region = countryToRegion(userCountry);
  const tEmeaPrivacy = useTranslations("emeaPatientPrivacyPolicy");
  const tEmeaTos = useTranslations("emeaPatientTOS");

  const max479w = useMedia("(max-width: 479px)");
  const max425px = useMedia("(max-width: 425px)");

  const [acceptedTerms, setAcceptedTerms] = useState<string[]>([]);
  const [step, setStep] = useState("privacy-policy");

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalType("");
  };

  const openPrivacy = () => {
    setModalOpen(true);
    setModalType("patient-privacy");
  };

  const openTermsOfUse = () => {
    setModalOpen(true);
    setModalType("terms-of-use");
  };

  // Specific policy/terms opened from the clickable names in the Collection
  // Statement (keyed by the tag names in collectionStatement.para2).
  const docModals: Record<
    string,
    { title: string; content: React.ReactElement }
  > = {
    usTos: {
      title: tTerms("usTermsOfUse.modalTitle"),
      content: <USTermsOfUseContent />,
    },
    anzTos: {
      title: tTerms("anzTermsOfUse.modalTitle"),
      content: <ANZTermsOfUseContent />,
    },
    emeaTos: {
      title: tEmeaTos("title"),
      content: (
        <ModalScroll>
          <EMEAPatientTermsOfUseContent />
        </ModalScroll>
      ),
    },
    usPrivacy: {
      title: tTerms("usPatientPrivacyPolicy.title"),
      content: <USPatientPrivacyPolicyContent />,
    },
    anzPrivacy: {
      title: tTerms("anzPrivacyPolicy.title"),
      content: <ANZPrivacyPolicyContent />,
    },
    emeaPrivacy: {
      title: tEmeaPrivacy("title"),
      content: (
        <ModalScroll>
          <EMEAPatientPrivacyContent />
        </ModalScroll>
      ),
    },
  };

  const userRole = userProfileQuery.data?.role;

  const handleSubmitAcceptedTerms = () => {
    if (!patientQuery.data) return;

    const updatedPatient: Patient = {
      ...patientQuery.data,
      terms_of_acceptance: {
        accepted: acceptedTerms.length === 3,
        accepted_date:
          acceptedTerms.length === 3 ? new Date().toISOString() : undefined,
        collection_statement_version: "v1.0",
        privacy_policy_version: "v1.0",
        terms_of_use_version: "v1.0",
      },
    };

    updateToS(
      {
        patientId: patientQuery.data.patient_id!,
        patient: updatedPatient,
      },
      {
        onSuccess: (data) => {
          console.log("ToS updated successfully:", data);
        },
        onError: () => {
          toast.error(tToast("errors.failedUpdatePatientTerms"));
        },
      }
    );
  };

  if (userRole === "patient") {
    if (patientQuery.isLoading || userProfileQuery.isLoading) {
      return <Splash message="Loading..." />;
    }

    if (patientQuery.isError || userProfileQuery.isError) {
      toast.error(tToast("errors.failedUser"));
    }

    if (!patientQuery.data?.terms_of_acceptance?.accepted) {
      return (
        <>
          <AcceptTermsContainer>
            <Image
              src={DePuySynthesLogo.src}
              alt="DePuy Synthes"
              width={280}
              height={42}
              style={{
                padding: "0 20px",
                width: "100%",
                height: "auto",
                maxHeight: "56px",
                maxWidth: "280px",
                objectFit: "contain",
              }}
            />

            {step === "privacy-policy" && (
              <div className="terms-container">
                <div className="privacy">
                  {userCountry === "US" ? (
                    <USPatientPrivacyPolicyContent />
                  ) : region === "emea" ? (
                    <EMEAPatientPrivacyContent />
                  ) : (
                    <ANZPrivacyPolicyContent />
                  )}
                </div>
                <Button
                  label={tComponentText("next")}
                  name="next"
                  theme="primary"
                  onPress={() => {
                    setAcceptedTerms([...acceptedTerms, "privacy_policy"]);
                    setStep("terms-of-use");
                  }}
                />
              </div>
            )}
            {step === "terms-of-use" && (
              <div className="terms-container">
                <div className="privacy">
                  {userCountry === "US" ? (
                    <USTermsOfUseContent />
                  ) : region === "emea" ? (
                    <EMEAPatientTermsOfUseContent />
                  ) : (
                    <ANZTermsOfUseContent />
                  )}
                </div>
                <Button
                  label={tComponentText("next")}
                  name="next"
                  theme="primary"
                  onPress={() => {
                    setAcceptedTerms([...acceptedTerms, "terms_of_use"]);
                    setStep("collection-statement");
                  }}
                />
              </div>
            )}
            {step === "collection-statement" && (
              <div className="terms-container">
                <div className="collection">
                  <CollectionStatement
                    onOpenDoc={(doc) => {
                      setModalType(doc);
                      setModalOpen(true);
                    }}
                  />
                </div>
                <div className="final-container">
                  <Checkbox
                    label={
                      <p>
                        {tTerms("collectionstatement.confirm")}
                        <span
                          className="on-click-link"
                          onClick={openTermsOfUse}
                        >
                          {tTerms("collectionstatement.tos")}
                        </span>
                        <span className="on-click-link" onClick={openPrivacy}>
                          {tTerms("collectionstatement.privacyPolicy")}
                        </span>
                        {tTerms("collectionstatement.confirm2")}
                      </p>
                    }
                    toggled={acceptedTerms.includes("collection_statement")}
                    clickedFunction={(e) => {
                      if (e.target.checked) {
                        setAcceptedTerms([
                          ...acceptedTerms,
                          "collection_statement",
                        ]);
                      } else {
                        const updatedTerms = acceptedTerms.filter(
                          (term) => term !== "collection_statement"
                        );
                        setAcceptedTerms(updatedTerms);
                      }
                    }}
                    size={max425px ? "18px" : "25px"}
                    iconSize={max425px ? "18px" : "15px"}
                    style={{ alignItems: max479w ? "flex-start" : "center" }}
                  />
                  <Button
                    label={tComponentText("finish")}
                    name="finish"
                    theme="primary"
                    icon={isSubmittingTerms ? "loader" : undefined}
                    iconColor="#ffffff"
                    onPress={() => handleSubmitAcceptedTerms()}
                    isDisabled={
                      !acceptedTerms.includes("collection_statement") ||
                      isSubmittingTerms
                    }
                  />
                </div>
              </div>
            )}
          </AcceptTermsContainer>
          {/* Need to dynamically switch between US & ANZ */}
          {isModalOpen && modalType === "patient-privacy" && (
            <Modal
              title={
                region === "emea"
                  ? tEmeaPrivacy("title")
                  : tTerms("usPatientPrivacyPolicy.title")
              }
              showtimesIcon
              needFullScreen={max425px}
              modalWidth="85vw"
              content={
                userCountry === "US" ? (
                  <USPatientPrivacyPolicyContent />
                ) : region === "emea" ? (
                  <ModalScroll>
                    <EMEAPatientPrivacyContent />
                  </ModalScroll>
                ) : (
                  <ANZPrivacyPolicyContent />
                )
              }
              toggleModal={handleCloseModal}
            />
          )}
          {/* Dynamically switch between US, EMEA & ANZ */}
          {isModalOpen && modalType === "terms-of-use" && (
            <Modal
              title={
                region === "emea"
                  ? tEmeaTos("title")
                  : tTerms("usTermsOfUse.modalTitle")
              }
              showtimesIcon
              needFullScreen={max425px}
              modalWidth="85vw"
              content={
                userCountry === "US" ? (
                  <USTermsOfUseContent />
                ) : region === "emea" ? (
                  <ModalScroll>
                    <EMEAPatientTermsOfUseContent />
                  </ModalScroll>
                ) : (
                  <ANZTermsOfUseContent />
                )
              }
              toggleModal={handleCloseModal}
            />
          )}
          {/* Specifically opening within a modal */}
          {isModalOpen && modalType === "anz-privacy-policy" && (
            <Modal
              title={tTerms("anzPrivacyPolicy.title")}
              showtimesIcon
              needFullScreen={max425px}
              modalWidth="85vw"
              content={<ANZPrivacyPolicyContent />}
              toggleModal={handleCloseModal}
            />
          )}
          {isModalOpen && docModals[modalType] && (
            <Modal
              title={docModals[modalType].title}
              showtimesIcon
              needFullScreen={max425px}
              modalWidth="85vw"
              content={docModals[modalType].content}
              toggleModal={handleCloseModal}
            />
          )}
        </>
      );
    }
  }

  return <>{children}</>;
}
